/**
 * Small SVG shape paths in millimetres. Every coordinate is rounded to 0.01 mm so the
 * server render and the browser agree to the last digit (trigonometry can differ in the
 * final bits between engines).
 */

import { r2 } from "@/lib/tool-kit/printables/page";

function point(cx: number, cy: number, radius: number, angle: number): string {
  return `${r2(cx + radius * Math.cos(angle))} ${r2(cy + radius * Math.sin(angle))}`;
}

/** A star with `points` tips, tip radius `outer` and valley radius `inner`, tip up. */
export function starPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number = outer * 0.4,
  points = 5
): string {
  const parts: string[] = [];
  for (let i = 0; i < points * 2; i += 1) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    parts.push(`${i === 0 ? "M" : "L"}${point(cx, cy, radius, angle)}`);
  }
  return `${parts.join("")}Z`;
}

/** A heart `size` wide, centred on (cx, cy). */
export function heartPath(cx: number, cy: number, size: number): string {
  const s = size / 2;
  const top = cy - s * 0.55;
  return [
    `M${r2(cx)} ${r2(cy + s * 0.9)}`,
    `C${r2(cx - s * 1.2)} ${r2(cy + s * 0.1)} ${r2(cx - s * 1.1)} ${r2(top - s * 0.6)} ${r2(cx - s * 0.5)} ${r2(top - s * 0.45)}`,
    `C${r2(cx - s * 0.2)} ${r2(top - s * 0.4)} ${r2(cx)} ${r2(top - s * 0.1)} ${r2(cx)} ${r2(top + s * 0.15)}`,
    `C${r2(cx)} ${r2(top - s * 0.1)} ${r2(cx + s * 0.2)} ${r2(top - s * 0.4)} ${r2(cx + s * 0.5)} ${r2(top - s * 0.45)}`,
    `C${r2(cx + s * 1.1)} ${r2(top - s * 0.6)} ${r2(cx + s * 1.2)} ${r2(cy + s * 0.1)} ${r2(cx)} ${r2(cy + s * 0.9)}Z`,
  ].join("");
}

/** A scalloped rosette: `teeth` points alternating between `outer` and `inner` radius. */
export function rosettePath(
  cx: number,
  cy: number,
  outer: number,
  teeth = 24,
  depth = 0.9
): string {
  return starPath(cx, cy, outer, outer * depth, teeth);
}

/** A quarter-circle arc used for corner ornaments. */
export function cornerArc(
  x: number,
  y: number,
  radius: number,
  corner: "tl" | "tr" | "bl" | "br"
): string {
  const rr = r2(radius);
  switch (corner) {
    case "tl":
      return `M${r2(x + radius)} ${r2(y)}A${rr} ${rr} 0 0 0 ${r2(x)} ${r2(y + radius)}`;
    case "tr":
      return `M${r2(x - radius)} ${r2(y)}A${rr} ${rr} 0 0 1 ${r2(x)} ${r2(y + radius)}`;
    case "bl":
      return `M${r2(x + radius)} ${r2(y)}A${rr} ${rr} 0 0 1 ${r2(x)} ${r2(y - radius)}`;
    default:
      return `M${r2(x - radius)} ${r2(y)}A${rr} ${rr} 0 0 0 ${r2(x)} ${r2(y - radius)}`;
  }
}
